import Phaser from "phaser";
import { Player } from "@/entities/Player";
import { PetFollower } from "@/entities/PetFollower";
import { Monster } from "@/entities/Monster";
import { ZenPile } from "@/entities/ZenPile";
import { EquipPile } from "@/entities/EquipPile";
import { ensureRealtime } from "@/network/realtime";
import { Pet } from "@/network/RestApi";
import { useCharacterStore } from "@/state/characterStore";
import { useUIStore } from "@/state/uiStore";
import { useTeleportStore } from "@/state/teleportStore";
import { isPhaserPointerOverBlockingUI } from "@/ui/pointerBlock";
import { CooldownTracker } from "@/systems/CombatSystem";
import { chat } from "@/systems/ChatSystem";
import type { NearbyChatMsg } from "@/systems/ChatSystem";
import { useSkillStore } from "@/state/skillStore";
import { inventory } from "@/systems/InventorySystem";
import type {
  FieldSnapshot,
  MonsterHitMsg,
  PlayerAttackMsg,
  ZenDropPush,
  ZenPickupPush,
  EquipDropPush,
  EquipPickupPush,
  EliteSpawnPush,
  EliteDropPush,
  PlayerDeathPush,
  PlayerRespawnPush,
} from "@/network/messages";

import {
  FIELD_PIX_W,
  FIELD_PIX_H,
  JOIN,
  MOVE,
  STATE,
  classRange,
  MONSTER_ATTACK_RANGE,
  type FieldTheme,
  type MapKey,
  type SkillTpl,
} from "./field/FieldConstants";
import { TEX } from "@/data/assetManifest";
import { AudioManager } from "@/systems/AudioManager";
import { getSettingsSnapshot } from "@/state/settingsStore";
import { getAdminSnapshot } from "@/state/adminStore";
import { buildFieldTilemap } from "./field/FieldTilemap";
import { hasMapModule, loadMapModule } from "@/maps/registry";
import { compile } from "@/maps/types";
import { renderMapModule } from "@/maps/runtime";
import { ensureMapBase } from "@/maps/baseLoader";
import { AmbientLightSystem } from "@/systems/AmbientLightSystem";
import { WeatherFXSystem } from "@/systems/WeatherFXSystem";
import { PlayerLightSystem } from "@/systems/PlayerLightSystem";
import { createFieldCombat, type FieldCombat } from "./field/FieldCombat";
import {
  createFieldAutomation,
  type FieldAutomation,
} from "./field/FieldAutomation";
import {
  onState,
  onMonsterHit,
  onPlayerAttack,
  onZenDrop,
  onZenPickup,
  onEquipDrop,
  onEquipPickup,
  onEliteSpawn,
  onEliteDrop,
} from "./field/FieldNetworkHandlers";

export class FieldScene extends Phaser.Scene {
  // Public state — read/written by helper modules in ./field/*.
  public players = new Map<string, Player>();
  public monsters = new Map<number, Monster>();
  public deadMonsters = new Set<number>();
  public zenPiles = new Map<number, ZenPile>();
  public equipPiles = new Map<number, EquipPile>();
  public pickupsInFlight = new Set<number>();
  public equipPickupsInFlight = new Set<number>();
  public pickupWalkTargetId?: number;
  public equipPickupWalkTargetId?: number;
  public localPlayer?: Player;
  public localUid = "";
  public cd = new CooldownTracker();
  public readonly BASIC_ATTACK_ID = 0;
  public readonly BASIC_ATTACK_CD_MS = 600;
  public readonly PICKUP_RANGE = 56;
  public lastSent = 0;
  public targetMonster?: Monster;
  public autoAttack = false;
  public dead = false;
  public autoPotInFlight = false;
  public nextAutoPotMs = 0;
  public inventoryRefreshAt = 0;

  public combat!: FieldCombat;
  public automation!: FieldAutomation;

  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd!: Record<"W" | "A" | "S" | "D", Phaser.Input.Keyboard.Key>;
  private hotkeys!: Phaser.Input.Keyboard.Key[];
  private spaceKey!: Phaser.Input.Keyboard.Key;
  private domKeyHandler?: (e: KeyboardEvent) => void;
  private unsubState?: () => void;
  private unsubNearby?: () => void;
  private petFollower?: PetFollower;
  private ambient?: AmbientLightSystem;
  private weatherFx?: WeatherFXSystem;
  private playerLight?: PlayerLightSystem;
  private unsubMonHit?: () => void;
  private unsubPlayerAttack?: () => void;
  private unsubZenDrop?: () => void;
  private unsubZenPickup?: () => void;
  private unsubEquipDrop?: () => void;
  private unsubEquipPickup?: () => void;
  private unsubEliteSpawn?: () => void;
  private unsubEliteDrop?: () => void;
  private unsubPlayerDeath?: () => void;
  private unsubPlayerRespawn?: () => void;

  private gateZones: Array<{
    px: number;
    py: number;
    radius: number;
    target: "Town" | "Field" | "Dungeon";
    targetMapId: number;
    spawnX: number;
    spawnY: number;
    minLv: number;
    label: string;
  }> = [];
  private gateCooldownUntil = 0;
  private blockers: Array<{ x: number; y: number; w: number; h: number }> = [];
  private mapGates: Array<{
    x: number; y: number; radius: number;
    target: "Town" | "Field" | "Dungeon";
    targetMapId: number;
    spawnX: number; spawnY: number;
    minLv: number;
    label?: string;
  }> = [];

  constructor() {
    super("Field");
  }

  private async loadCollisionData(mapKey: FieldTheme) {
    const collisionFiles: Record<
      string,
      () => Promise<{
        default: {
          blockers?: Array<{
            px: { x: number; y: number; w: number; h: number };
          }>;
        };
      }>
    > = {
      noria: () => import("@/data/maps/noria.collision.json"),
    };
    const loader = collisionFiles[mapKey];
    if (!loader) return;
    try {
      const mod = await loader();
      if (mod?.default?.blockers) {
        this.blockers = mod.default.blockers.map((b) => b.px);
      }
    } catch {
      // Collision data not available — movement uses world-bounds only
    }
  }

  private isBlocked(px: number, py: number, radius = 12): boolean {
    for (const b of this.blockers) {
      if (
        px + radius > b.x &&
        px - radius < b.x + b.w &&
        py + radius > b.y &&
        py - radius < b.y + b.h
      ) {
        return true;
      }
    }
    return false;
  }

  /** Slide-along collision: returns the blocked position or original if stuck. */
  public collide(
    oldX: number,
    oldY: number,
    nx: number,
    ny: number,
  ): { x: number; y: number } {
    if (this.blockers.length === 0 || this.isBlocked(oldX, oldY))
      return { x: nx, y: ny };
    let cx = nx,
      cy = ny;
    if (this.isBlocked(cx, oldY)) cx = oldX;
    if (this.isBlocked(oldX, cy)) cy = oldY;
    if (cx !== oldX && cy !== oldY && this.isBlocked(cx, cy)) {
      cx = oldX;
      cy = oldY;
    }
    return { x: cx, y: cy };
  }

  async create() {
    // Reset per-instance state — Phaser reuses scene instances on restart.
    this.dead = false;
    this.autoAttack = false;
    this.targetMonster = undefined;
    this.blockers = [];
    this.mapGates = [];
    this.combat = createFieldCombat(this);
    this.automation = createFieldAutomation(this);

    void useSkillStore.getState().fetch();
    // Pick theme from current map id.
    const curMapId = useCharacterStore.getState().current?.mapId ?? 2;
    const themeByMapId: Record<number, MapKey> = {
      1: "lorencia",
      2: "noria",
      3: "devias",
      4: "dungeon",
      5: "atlans",
      6: "tarkan",
      7: "icarus",
      8: "kalima",
      9: "blood_castle",
      10: "devil_square",
      11: "chaos_castle",
      12: "illusion_temple",
      13: "crywolf",
      14: "castle_siege",
      15: "golden_invasion",
      16: "hero_dungeon",
      100: "lorencia_outskirt",
    };
    const mapKey: MapKey = themeByMapId[curMapId] ?? "noria";
    // Lazy-load this map's base PNG (if it has one) before rendering. Other
    // maps' base images are not pulled into memory.
    await ensureMapBase(this, mapKey);
    if (hasMapModule(mapKey)) {
      const node = await loadMapModule(mapKey);
      if (node) {
        const mod = compile(node);
        const rendered = renderMapModule(this, mod, {
          paintProcedural: (sc, theme) => {
            const known: Record<string, true> = {
              lorencia_outskirt: true,
              noria: true,
              devias: true,
              atlans: true,
              tarkan: true,
              icarus: true,
              kalima: true,
              crywolf: true,
            };
            if (known[theme]) buildFieldTilemap(sc, theme as FieldTheme);
          },
        });
        this.blockers = rendered.blockers;
        this.mapGates = mod.gates.map((g) => ({
          x: g.x, y: g.y, radius: g.radius,
          target: g.target, targetMapId: g.targetMapId,
          spawnX: g.spawnX, spawnY: g.spawnY,
          minLv: g.minLv, label: g.label,
        }));
      } else {
        buildFieldTilemap(this, mapKey as FieldTheme);
        await this.loadCollisionData(mapKey as FieldTheme);
      }
    } else {
      buildFieldTilemap(this, mapKey as FieldTheme);
      await this.loadCollisionData(mapKey as FieldTheme);
    }
    AudioManager.playForMap(this, curMapId);
    const themeLabel: Record<MapKey, string> = {
      lorencia: "Lorencia",
      lorencia_outskirt: "Vùng Ngoại Ô Lorencia",
      noria: "Noria (Forest)",
      devias: "Devias (Snow)",
      atlans: "Atlans (Underwater)",
      tarkan: "Tarkan (Desert)",
      icarus: "Icarus (Sky Islands)",
      kalima: "Kalima (Shadow Realm)",
      crywolf: "Crywolf Fortress",
      dungeon: "Dungeon",
      blood_castle: "Blood Castle",
      devil_square: "Devil Square",
      chaos_castle: "Chaos Castle",
      illusion_temple: "Illusion Temple",
      castle_siege: "Castle Siege",
      golden_invasion: "Golden Invasion",
      hero_dungeon: "Hero Dungeon",
    };
    this.add
      .text(20, 20, themeLabel[mapKey], {
        fontFamily: '"Philosopher", "Cinzel", serif',
        fontSize: "18px",
        color: "#fff",
        stroke: "#000",
        strokeThickness: 3,
      })
      .setScrollFactor(0)
      .setDepth(100);
    this.cameras.main.setBounds(0, 0, FIELD_PIX_W, FIELD_PIX_H);
    this.physics.world.setBounds(0, 0, FIELD_PIX_W, FIELD_PIX_H);

    this.cursors = this.input.keyboard!.createCursorKeys();
    this.wasd = this.input.keyboard!.addKeys("W,A,S,D") as typeof this.wasd;
    this.input.keyboard!.removeCapture("W,A,S,D,SPACE");
    this.hotkeys = ["ONE", "TWO", "THREE", "FOUR", "FIVE"].map((k) =>
      this.input.keyboard!.addKey(k),
    );
    this.hotkeys.forEach((k, i) =>
      k.on("down", () => this.combat.castHotkey(i)),
    );
    this.spaceKey = this.input.keyboard!.addKey("SPACE");
    this.spaceKey.on("down", () => {
      this.combat.basicAttack();
    });
    // Note: T (auto-attack) is handled only via the DOM keydown fallback below.

    this.domKeyHandler = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.isContentEditable)
      )
        return;
      if (e.code === "Space") {
        e.preventDefault();
        this.combat.basicAttack();
      } else if (e.code.startsWith("Digit")) {
        const n = parseInt(e.code.replace("Digit", ""), 10);
        if (n >= 1 && n <= 5) this.combat.castHotkey(n - 1);
      } else if (e.code === "KeyT") {
        e.preventDefault();
        this.automation.toggleAutoAttack();
      }
    };
    window.addEventListener("keydown", this.domKeyHandler);

    const ch = useCharacterStore.getState().current;
    if (!ch) {
      this.scene.start("CharacterSelect");
      return;
    }

    const mapIdByTheme: Record<MapKey, number> = {
      lorencia: 1,
      lorencia_outskirt: 100,
      noria: 2,
      devias: 3,
      dungeon: 4,
      atlans: 5,
      tarkan: 6,
      icarus: 7,
      kalima: 8,
      blood_castle: 9,
      devil_square: 10,
      chaos_castle: 11,
      illusion_temple: 12,
      crywolf: 13,
      castle_siege: 14,
      golden_invasion: 15,
      hero_dungeon: 16,
    };
    // Per-theme default spawn point — placed in the middle of the map so the
    // player is comfortably away from every edge gate (auto-enter radius 56).
    // South gate is at y = FIELD_PIX_H - 48 = 1232; spawn must stay >= ~120 px
    // away from any gate to avoid instant re-entry on arrival.
    const defaultSpawnByTheme: Record<MapKey, { x: number; y: number }> = {
      lorencia: { x: 1280, y: 1280 },
      lorencia_outskirt: { x: 960, y: 200 },
      noria: { x: 960, y: 900 },
      devias: { x: 960, y: 900 },
      dungeon: { x: 960, y: 900 },
      atlans: { x: 960, y: 900 },
      tarkan: { x: 960, y: 900 },
      icarus: { x: 960, y: 900 },
      kalima: { x: 960, y: 900 },
      blood_castle: { x: 960, y: 900 },
      devil_square: { x: 960, y: 900 },
      chaos_castle: { x: 960, y: 900 },
      illusion_temple: { x: 960, y: 900 },
      crywolf: { x: 960, y: 900 },
      castle_siege: { x: 960, y: 900 },
      golden_invasion: { x: 960, y: 900 },
      hero_dungeon: { x: 960, y: 900 },
    };
    const def = defaultSpawnByTheme[mapKey] ?? { x: 500, y: 700 };
    const DEFAULT_FIELD_SPAWN_X = def.x;
    const DEFAULT_FIELD_SPAWN_Y = def.y;
    // Only restore the saved position when it is far enough from every map
    // edge to avoid the "spawn-on-gate-then-instantly-bounce" loop. If the
    // last position was hugging an edge (e.g. the player walked into a gate
    // or was logged out near one), fall back to the safe default spawn.
    const SAFE_EDGE_MARGIN = 160;
    const savedFar =
      (ch.posX || 0) > SAFE_EDGE_MARGIN &&
      (ch.posX || 0) < FIELD_PIX_W - SAFE_EDGE_MARGIN &&
      (ch.posY || 0) > SAFE_EDGE_MARGIN &&
      (ch.posY || 0) < FIELD_PIX_H - SAFE_EDGE_MARGIN;
    const useSaved =
      ch.mapId === mapIdByTheme[mapKey] &&
      (ch.posX || 0) > 0 &&
      (ch.posY || 0) > 0 &&
      savedFar;
    const FIELD_SPAWN_X = useSaved ? ch.posX : DEFAULT_FIELD_SPAWN_X;
    const FIELD_SPAWN_Y = useSaved ? ch.posY : DEFAULT_FIELD_SPAWN_Y;
    useCharacterStore.getState().setPosition({
      x: FIELD_SPAWN_X,
      y: FIELD_SPAWN_Y,
      mapId: mapIdByTheme[mapKey],
    });
    useTeleportStore.getState().markVisited(mapIdByTheme[mapKey]);

    try {
      const cli = await ensureRealtime();
      const spawnX = FIELD_SPAWN_X;
      const spawnY = FIELD_SPAWN_Y;
      const resp = await cli.request<{ ok: boolean; uid: string }>(JOIN, {
        name: ch.name,
        klass: ch.class,
        charId: ch.id,
        x: spawnX,
        y: spawnY,
        mapId: mapIdByTheme[mapKey],
      });
      this.localUid = resp.uid;
      this.spawnLocal(spawnX, spawnY);
      inventory.setChar(ch.id);
      inventory.refresh().catch(() => {
        /* swallow */
      });
      this.unsubState = cli.on(STATE, (data) =>
        onState(this, data as FieldSnapshot),
      );
      this.unsubMonHit = cli.on("monster_hit", (data) =>
        onMonsterHit(this, data as MonsterHitMsg),
      );
      this.unsubPlayerAttack = cli.on("player_attack", (data) =>
        onPlayerAttack(this, data as PlayerAttackMsg),
      );
      this.unsubZenDrop = cli.on("zen_drop", (data) =>
        onZenDrop(this, data as ZenDropPush),
      );
      this.unsubZenPickup = cli.on("zen_pickup", (data) =>
        onZenPickup(this, data as ZenPickupPush),
      );
      this.unsubEquipDrop = cli.on("equip_drop", (data) =>
        onEquipDrop(this, data as EquipDropPush),
      );
      this.unsubEquipPickup = cli.on("equip_pickup", (data) =>
        onEquipPickup(this, data as EquipPickupPush),
      );
      this.unsubEliteSpawn = cli.on("elite_spawn", (data) =>
        onEliteSpawn(this, data as EliteSpawnPush),
      );
      this.unsubEliteDrop = cli.on("elite_drop", (data) =>
        onEliteDrop(this, data as EliteDropPush),
      );
      this.unsubPlayerDeath = cli.on("player_death", (data) =>
        this.combat.onServerDeath(data as PlayerDeathPush),
      );
      this.unsubPlayerRespawn = cli.on("player_respawn", (data) =>
        this.combat.onServerRespawn(data as PlayerRespawnPush),
      );
      chat.setRoom("field");
      chat.setLocalUid(this.localUid);
      chat.bindWebSocket();
      this.unsubNearby = chat.onNearbyChat((m: NearbyChatMsg) => {
        this.players.get(m.uid)?.showBubble(m.text);
      });
    } catch (e) {
      useUIStore
        .getState()
        .showToast(`Kết nối field thất bại: ${(e as Error).message}`, "error");
    }

    this.spawnGates(mapKey);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubState?.();
      this.unsubNearby?.();
      this.petFollower?.destroy();
      this.petFollower = undefined;
      this.unsubMonHit?.();
      this.unsubPlayerAttack?.();
      this.unsubZenDrop?.();
      this.unsubZenPickup?.();
      this.unsubEquipDrop?.();
      this.unsubEquipPickup?.();
      this.unsubEliteSpawn?.();
      this.unsubEliteDrop?.();
      this.unsubPlayerDeath?.();
      this.unsubPlayerRespawn?.();
      this.ambient?.destroy();
      this.ambient = undefined;
      this.weatherFx?.destroy();
      this.weatherFx = undefined;
      this.playerLight?.destroy();
      this.playerLight = undefined;
      chat.unbind();
      ensureRealtime()
        .then((cli) => cli.notify("field.leave", {}))
        .catch(() => {});
      if (this.domKeyHandler) {
        window.removeEventListener("keydown", this.domKeyHandler);
        this.domKeyHandler = undefined;
      }
    });

    this.ambient = new AmbientLightSystem(this);
    this.weatherFx = new WeatherFXSystem(this);
    this.playerLight = new PlayerLightSystem(this);
  }

  private spawnGates(mapKey: MapKey) {
    // Per-theme gate configuration. The progression chain is:
    //   Lorencia (Town) <-> Noria <-> Devias <-> Atlans <-> Tarkan <-> Icarus
    //                                  |                       |
    //                                  +-- Kalima (event)      +-- Crywolf (event)
    // South = previous map, North = next map. East/West = branch (event/dungeon).
    type GateCfg = {
      px: number;
      py: number;
      target: "Town" | "Field" | "Dungeon";
      targetMapId: number;
      spawnX: number;
      spawnY: number;
      minLv: number;
      label: string;
      tex: string;
    };
    const south = (cfg: Omit<GateCfg, "px" | "py" | "tex">): GateCfg => ({
      ...cfg,
      px: FIELD_PIX_W / 2,
      py: FIELD_PIX_H - 48,
      tex: TEX.gateSouth,
    });
    const north = (cfg: Omit<GateCfg, "px" | "py" | "tex">): GateCfg => ({
      ...cfg,
      px: FIELD_PIX_W / 2,
      py: 48,
      tex: TEX.gateNorth,
    });
    const east = (cfg: Omit<GateCfg, "px" | "py" | "tex">): GateCfg => ({
      ...cfg,
      px: FIELD_PIX_W - 48,
      py: FIELD_PIX_H / 2,
      tex: TEX.gateEast,
    });
    const west = (cfg: Omit<GateCfg, "px" | "py" | "tex">): GateCfg => ({
      ...cfg,
      px: 48,
      py: FIELD_PIX_H / 2,
      tex: TEX.gateWest,
    });

    const gates: GateCfg[] = [];

    // If the .map.tsx module declared gates, use those as the source of truth
    // (editable via the in-game Map Editor). The hardcoded fallback below only
    // runs for maps that don't yet have <Gate> elements in their module.
    if (this.mapGates.length > 0) {
      for (const g of this.mapGates) {
        // Pick a directional fallback texture based on which edge the gate is
        // closest to (only used if propPortalGateIdle isn't loaded).
        const dx = g.x - FIELD_PIX_W / 2;
        const dy = g.y - FIELD_PIX_H / 2;
        let tex: string = TEX.gateNorth;
        if (Math.abs(dx) > Math.abs(dy)) tex = dx > 0 ? TEX.gateEast : TEX.gateWest;
        else tex = dy > 0 ? TEX.gateSouth : TEX.gateNorth;
        gates.push({
          px: g.x, py: g.y,
          target: g.target,
          targetMapId: g.targetMapId,
          spawnX: g.spawnX,
          spawnY: g.spawnY,
          minLv: g.minLv,
          label: g.label ?? "",
          tex,
        });
      }
    } else if (mapKey === "lorencia_outskirt") {
      gates.push(
        north({
          target: "Town",
          targetMapId: 1,
          spawnX: 1280,
          spawnY: 2432,
          minLv: 1,
          label: "Cổng Bắc\n[Lorencia]",
        }),
      );
    } else if (mapKey === "noria") {
      gates.push(
        south({
          target: "Town",
          targetMapId: 1,
          spawnX: 1280,
          spawnY: 1280,
          minLv: 1,
          label: "Cổng Nam\n[Lorencia]",
        }),
      );
      gates.push(
        north({
          target: "Field",
          targetMapId: 3,
          spawnX: 960,
          spawnY: 900,
          minLv: 30,
          label: "Cổng Bắc\n[Devias]",
        }),
      );
    } else if (mapKey === "devias") {
      gates.push(
        south({
          target: "Field",
          targetMapId: 2,
          spawnX: 960,
          spawnY: 900,
          minLv: 1,
          label: "Cổng Nam\n[Noria]",
        }),
      );
      gates.push(
        north({
          target: "Field",
          targetMapId: 5,
          spawnX: 960,
          spawnY: 900,
          minLv: 150,
          label: "Cổng Bắc\n[Atlans]",
        }),
      );
      gates.push(
        west({
          target: "Dungeon",
          targetMapId: 9,
          spawnX: 100,
          spawnY: 360,
          minLv: 80,
          label: "Cổng Tây\n[Blood Castle]",
        }),
      );
      gates.push(
        east({
          target: "Dungeon",
          targetMapId: 11,
          spawnX: 100,
          spawnY: 360,
          minLv: 100,
          label: "Cổng Đông\n[Chaos Castle]",
        }),
      );
    } else if (mapKey === "atlans") {
      gates.push(
        south({
          target: "Field",
          targetMapId: 3,
          spawnX: 960,
          spawnY: 900,
          minLv: 30,
          label: "Cổng Nam\n[Devias]",
        }),
      );
      gates.push(
        north({
          target: "Field",
          targetMapId: 6,
          spawnX: 960,
          spawnY: 900,
          minLv: 250,
          label: "Cổng Bắc\n[Tarkan]",
        }),
      );
      gates.push(
        east({
          target: "Dungeon",
          targetMapId: 12,
          spawnX: 100,
          spawnY: 360,
          minLv: 220,
          label: "Cổng Đông\n[Illusion Temple]",
        }),
      );
    } else if (mapKey === "tarkan") {
      gates.push(
        south({
          target: "Field",
          targetMapId: 5,
          spawnX: 960,
          spawnY: 900,
          minLv: 150,
          label: "Cổng Nam\n[Atlans]",
        }),
      );
      gates.push(
        north({
          target: "Field",
          targetMapId: 7,
          spawnX: 960,
          spawnY: 900,
          minLv: 350,
          label: "Cổng Bắc\n[Icarus]",
        }),
      );
      gates.push(
        east({
          target: "Field",
          targetMapId: 13,
          spawnX: 960,
          spawnY: 900,
          minLv: 280,
          label: "Cổng Đông\n[Crywolf]",
        }),
      );
      gates.push(
        west({
          target: "Field",
          targetMapId: 8,
          spawnX: 960,
          spawnY: 900,
          minLv: 320,
          label: "Cổng Tây\n[Kalima]",
        }),
      );
    } else if (mapKey === "icarus") {
      gates.push(
        south({
          target: "Field",
          targetMapId: 6,
          spawnX: 960,
          spawnY: 900,
          minLv: 250,
          label: "Cổng Nam\n[Tarkan]",
        }),
      );
      gates.push(
        north({
          target: "Dungeon",
          targetMapId: 16,
          spawnX: 100,
          spawnY: 360,
          minLv: 400,
          label: "Cổng Bắc\n[Hero Dungeon]",
        }),
      );
      gates.push(
        east({
          target: "Field",
          targetMapId: 14,
          spawnX: 960,
          spawnY: 900,
          minLv: 350,
          label: "Cổng Đông\n[Castle Siege]",
        }),
      );
    } else if (mapKey === "kalima") {
      gates.push(
        south({
          target: "Field",
          targetMapId: 6,
          spawnX: 960,
          spawnY: 900,
          minLv: 250,
          label: "Cổng Về\n[Tarkan]",
        }),
      );
    } else if (mapKey === "crywolf") {
      gates.push(
        south({
          target: "Field",
          targetMapId: 6,
          spawnX: 960,
          spawnY: 900,
          minLv: 250,
          label: "Cổng Về\n[Tarkan]",
        }),
      );
    }

    this.gateZones = [];
    for (const g of gates) {
      const color =
        g.target === "Town"
          ? 0xe74c3c
          : g.target === "Dungeon"
            ? 0x8e44ad
            : 0x3498db;
      const auraOuter = this.add
        .circle(g.px, g.py, 70, color, 0.18)
        .setDepth(4);
      const auraInner = this.add
        .circle(g.px, g.py, 44, color, 0.32)
        .setDepth(4);
      this.tweens.add({
        targets: [auraOuter, auraInner],
        scale: { from: 0.85, to: 1.15 },
        alpha: { from: 0.5, to: 0.9 },
        duration: 1100,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      });

      // Animated portal sprite (4-frame idle vortex). Falls back to procedural
      // directional banner texture if the spritesheet is missing.
      let gate: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite;
      if (this.textures.exists(TEX.propPortalGateIdle)) {
        if (!this.anims.exists("portal_gate_idle")) {
          this.anims.create({
            key: "portal_gate_idle",
            frames: this.anims.generateFrameNumbers(TEX.propPortalGateIdle, { start: 0, end: 3 }),
            frameRate: 5,
            repeat: -1,
          });
        }
        const sprite = this.add.sprite(g.px, g.py, TEX.propPortalGateIdle).setDepth(5);
        sprite.setDisplaySize(96, 96);
        sprite.play({ key: "portal_gate_idle", startFrame: Math.floor(Math.random() * 4) });
        gate = sprite;
      } else {
        gate = this.add.image(g.px, g.py, g.tex).setDepth(5);
      }
      gate.setInteractive({ useHandCursor: true });
      gate.on("pointerup", (pointer: Phaser.Input.Pointer) => {
        if (!isPhaserPointerOverBlockingUI(pointer)) this.tryEnterGate(g);
      });
      this.tweens.add({
        targets: gate,
        alpha: { from: 0.75, to: 1 },
        duration: 900 + Math.random() * 400,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      });

      const colorHex =
        g.target === "Town"
          ? "#e74c3c"
          : g.target === "Dungeon"
            ? "#8e44ad"
            : "#3498db";
      this.add
        .text(g.px, g.py + 52, g.label, {
          fontFamily: '"Philosopher", "Cinzel", serif',
          fontSize: "13px",
          color: colorHex,
          stroke: "#000",
          strokeThickness: 3,
          align: "center",
        })
        .setOrigin(0.5, 0)
        .setDepth(6);

      this.gateZones.push({
        px: g.px,
        py: g.py,
        radius: 56,
        target: g.target,
        targetMapId: g.targetMapId,
        spawnX: g.spawnX,
        spawnY: g.spawnY,
        minLv: g.minLv,
        label: g.label,
      });
    }
  }

  private tryEnterGate(g: {
    target: "Town" | "Field" | "Dungeon";
    targetMapId: number;
    spawnX: number;
    spawnY: number;
    minLv: number;
  }) {
    const ch = useCharacterStore.getState().current;
    if (ch && ch.level < g.minLv) {
      useUIStore.getState().showToast(`Cần đạt Lv.${g.minLv} để vào`, "warn");
      this.gateCooldownUntil = this.time.now + 2000;
      return;
    }
    useCharacterStore.getState().setPosition({
      x: g.spawnX,
      y: g.spawnY,
      mapId: g.targetMapId,
    });
    useTeleportStore.getState().markVisited(g.targetMapId);
    this.scene.start(g.target);
  }

  private spawnLocal(x: number, y: number) {
    // Guard against the scene being shut down while async create() awaited
    // realtime/join. Without this, re-entering the field can throw
    // "Cannot read properties of undefined (reading 'startFollow')".
    if (
      !this.scene ||
      !this.scene.isActive() ||
      !this.sys ||
      !this.sys.cameras
    ) {
      return;
    }
    const ch = useCharacterStore.getState().current!;
    this.localPlayer = new Player({
      scene: this,
      x,
      y,
      uid: this.localUid,
      name: ch.name,
      klass: ch.class,
      gender: ch.gender,
      isLocal: true,
    });
    this.players.set(this.localUid, this.localPlayer);
    if (this.cameras && this.cameras.main) {
      this.cameras.main.startFollow(this.localPlayer, true, 0.1, 0.1);
    }
    this.spawnPet();
  }

  private async spawnPet() {
    if (!this.localPlayer) return;
    const ch = useCharacterStore.getState().current;
    if (!ch) return;
    try {
      const { pets } = await Pet.list(ch.id);
      const active = pets?.find((p) => p.active);
      if (!active || !this.localPlayer) return;
      const { templates } = await Pet.templates();
      const tpl = templates?.find((t) => t.id === active.templateId);
      this.petFollower = new PetFollower({
        scene: this,
        owner: this.localPlayer,
        name: active.nickname || tpl?.name || `Pet`,
        tier: tpl?.tier ?? 1,
        petCode: tpl?.code,
      });
    } catch {
      // No pet or API unavailable
    }
  }

  update(time: number, delta: number) {
    if (this.petFollower) this.petFollower.update(time, delta);
    this.ambient?.update();
    this.weatherFx?.update(time, delta);
    if (this.localPlayer) this.playerLight?.setTarget(this.localPlayer);
    this.playerLight?.update(time);
    if (!this.localPlayer) return;
    for (const [uid, pl] of this.players) {
      if (uid !== this.localUid) pl.preUpdate(time, delta);
    }
    for (const mob of this.monsters.values()) mob.tick(delta);

    // Y-sort dynamic entities so they correctly render in front of/behind
    // map props (whose depth = bottom edge, p.y + p.h/2). Player container
    // origin sits near the feet (paperdoll origin y=0.94), so using
    // entity.y as depth aligns with the prop baseline. PetFollower manages
    // its own depth relative to the owner inside its update() above.
    for (const pl of this.players.values()) if (pl.active) pl.setDepth(pl.y);
    for (const mob of this.monsters.values())
      if (mob.active) mob.setDepth(mob.y);
    const el = document.activeElement;
    if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA")) return;
    if (this.dead) return;
    const speed = 0.2 * delta;
    let dx = 0,
      dy = 0;
    if (this.cursors.left.isDown || this.wasd.A.isDown) dx -= 1;
    if (this.cursors.right.isDown || this.wasd.D.isDown) dx += 1;
    if (this.cursors.up.isDown || this.wasd.W.isDown) dy -= 1;
    if (this.cursors.down.isDown || this.wasd.S.isDown) dy += 1;
    if (dx || dy) {
      const len = Math.hypot(dx, dy);
      let nx = Phaser.Math.Clamp(
        this.localPlayer.x + (dx / len) * speed,
        16,
        FIELD_PIX_W - 16,
      );
      let ny = Phaser.Math.Clamp(
        this.localPlayer.y + (dy / len) * speed,
        16,
        FIELD_PIX_H - 16,
      );
      // Collision: slide along blockers (try X then Y independently).
      // Skip check if player is already inside a blocker (let them escape).
      if (
        this.blockers.length > 0 &&
        !this.isBlocked(this.localPlayer.x, this.localPlayer.y)
      ) {
        if (this.isBlocked(nx, this.localPlayer.y)) nx = this.localPlayer.x;
        if (this.isBlocked(this.localPlayer.x, ny)) ny = this.localPlayer.y;
        if (this.isBlocked(nx, ny)) {
          nx = this.localPlayer.x;
          ny = this.localPlayer.y;
        }
      }
      this.localPlayer.x = nx;
      this.localPlayer.y = ny;
      const dir = dx > 0 ? 1 : dx < 0 ? 3 : dy > 0 ? 2 : 0;
      useCharacterStore
        .getState()
        .setPosition({ x: this.localPlayer.x, y: this.localPlayer.y });
      if (time - this.lastSent > 100) {
        this.lastSent = time;
        ensureRealtime()
          .then((cli) =>
            cli.notify(MOVE, {
              x: this.localPlayer!.x,
              y: this.localPlayer!.y,
              d: dir,
            }),
          )
          .catch(() => {
            /* swallow */
          });
      }
    }

    // Auto-enter gate if player walks into the aura.
    if (this.localPlayer && time >= this.gateCooldownUntil) {
      for (const z of this.gateZones) {
        const ddx = this.localPlayer.x - z.px;
        const ddy = this.localPlayer.y - z.py;
        if (ddx * ddx + ddy * ddy <= z.radius * z.radius) {
          this.gateCooldownUntil = time + 1500;
          this.tryEnterGate(z);
          return;
        }
      }
    }

    // Click-to-walk-and-pickup: if user clicked a far Zen pile, walk toward it.
    if (this.pickupWalkTargetId !== undefined) {
      const target = this.zenPiles.get(this.pickupWalkTargetId);
      if (!target || !target.alive) {
        this.pickupWalkTargetId = undefined;
      } else if (this.localPlayer) {
        const tdx = target.x - this.localPlayer.x;
        const tdy = target.y - this.localPlayer.y;
        const td2 = tdx * tdx + tdy * tdy;
        if (td2 <= this.PICKUP_RANGE * this.PICKUP_RANGE) {
          this.automation.tryPickup(this.pickupWalkTargetId);
          this.pickupWalkTargetId = undefined;
        } else if (!(dx || dy)) {
          const dist = Math.sqrt(td2) || 1;
          let nx = Phaser.Math.Clamp(
            this.localPlayer.x + (tdx / dist) * speed,
            16,
            FIELD_PIX_W - 16,
          );
          let ny = Phaser.Math.Clamp(
            this.localPlayer.y + (tdy / dist) * speed,
            16,
            FIELD_PIX_H - 16,
          );
          const c = this.collide(
            this.localPlayer.x,
            this.localPlayer.y,
            nx,
            ny,
          );
          nx = c.x;
          ny = c.y;
          this.localPlayer.x = nx;
          this.localPlayer.y = ny;
          const dir =
            Math.abs(tdx) > Math.abs(tdy) ? (tdx > 0 ? 1 : 3) : tdy > 0 ? 2 : 0;
          useCharacterStore.getState().setPosition({ x: nx, y: ny });
          if (time - this.lastSent > 100) {
            this.lastSent = time;
            ensureRealtime()
              .then((cli) => cli.notify(MOVE, { x: nx, y: ny, d: dir }))
              .catch(() => {
                /* swallow */
              });
          }
        } else {
          this.pickupWalkTargetId = undefined;
        }
      }
    }

    // Click-to-walk-and-pickup: equip pile (mirrors Zen pile flow).
    if (this.equipPickupWalkTargetId !== undefined) {
      const target = this.equipPiles.get(this.equipPickupWalkTargetId);
      if (!target || !target.alive) {
        this.equipPickupWalkTargetId = undefined;
      } else if (this.localPlayer) {
        const tdx = target.x - this.localPlayer.x;
        const tdy = target.y - this.localPlayer.y;
        const td2 = tdx * tdx + tdy * tdy;
        if (td2 <= this.PICKUP_RANGE * this.PICKUP_RANGE) {
          this.automation.tryPickupEquip(this.equipPickupWalkTargetId);
          this.equipPickupWalkTargetId = undefined;
        } else if (!(dx || dy)) {
          const dist = Math.sqrt(td2) || 1;
          let nx = Phaser.Math.Clamp(
            this.localPlayer.x + (tdx / dist) * speed,
            16,
            FIELD_PIX_W - 16,
          );
          let ny = Phaser.Math.Clamp(
            this.localPlayer.y + (tdy / dist) * speed,
            16,
            FIELD_PIX_H - 16,
          );
          const c = this.collide(
            this.localPlayer.x,
            this.localPlayer.y,
            nx,
            ny,
          );
          nx = c.x;
          ny = c.y;
          this.localPlayer.x = nx;
          this.localPlayer.y = ny;
          const dir =
            Math.abs(tdx) > Math.abs(tdy) ? (tdx > 0 ? 1 : 3) : tdy > 0 ? 2 : 0;
          useCharacterStore.getState().setPosition({ x: nx, y: ny });
          if (time - this.lastSent > 100) {
            this.lastSent = time;
            ensureRealtime()
              .then((cli) => cli.notify(MOVE, { x: nx, y: ny, d: dir }))
              .catch(() => {
                /* swallow */
              });
          }
        } else {
          this.equipPickupWalkTargetId = undefined;
        }
      }
    }

    // Auto-pickup nearby Zen piles owned by local or unowned-after-window.
    if (
      this.localPlayer &&
      this.zenPiles.size > 0 &&
      getSettingsSnapshot().autoPickupZen
    ) {
      const px = this.localPlayer.x;
      const py = this.localPlayer.y;
      const now = Date.now();
      for (const pile of this.zenPiles.values()) {
        if (!pile.alive) continue;
        if (this.pickupsInFlight.has(pile.pileId)) continue;
        const pdx = pile.x - px;
        const pdy = pile.y - py;
        if (pdx * pdx + pdy * pdy <= this.PICKUP_RANGE * this.PICKUP_RANGE) {
          if (
            pile.ownerUid &&
            pile.ownerUid !== this.localUid &&
            (!pile.ownerUntil || now < pile.ownerUntil)
          )
            continue;
          this.automation.tryPickup(pile.pileId);
          break;
        }
      }
    }

    // Auto-pickup nearby equip piles (same ownership rule as Zen).
    if (
      this.localPlayer &&
      this.equipPiles.size > 0 &&
      getSettingsSnapshot().autoPickupEquip
    ) {
      const px = this.localPlayer.x;
      const py = this.localPlayer.y;
      const now = Date.now();
      for (const pile of this.equipPiles.values()) {
        if (!pile.alive) continue;
        if (this.equipPickupsInFlight.has(pile.pileId)) continue;
        const pdx = pile.x - px;
        const pdy = pile.y - py;
        if (pdx * pdx + pdy * pdy <= this.PICKUP_RANGE * this.PICKUP_RANGE) {
          if (
            pile.ownerUid &&
            pile.ownerUid !== this.localUid &&
            (!pile.ownerUntil || now < pile.ownerUntil)
          )
            continue;
          this.automation.tryPickupEquip(pile.pileId);
          break;
        }
      }
    }

    // Auto-attack tick.
    if (this.autoAttack) {
      this.automation.tryAutoPotion(time);
      this.automation.autoWalkToNearestPile(delta, time);
      this.automation.autoWalkToNearestEquip(delta, time);

      if (!this.targetMonster || !this.targetMonster.alive) {
        const next = this.automation.findNearestMonster();
        if (next) {
          if (this.targetMonster && this.targetMonster !== next)
            this.targetMonster.setSelected(false);
          this.targetMonster = next;
          next.setSelected(true);
        } else {
          return;
        }
      }
      const skills: SkillTpl[] = this.combat.classSkills();
      const ch = useCharacterStore.getState().current;
      const klass = ch?.class ?? "warrior";
      const settings = getSettingsSnapshot();
      const autoCastSkill = settings.autoCastSkill;
      const whitelist = settings.autoCastSkillIds;
      const useWhitelist = whitelist.length > 0;
      let plannedSkill: SkillTpl | undefined;
      if (autoCastSkill) {
        for (let i = 0; i < skills.length; i++) {
          const sk = skills[i];
          if (sk.id === 303 || sk.id === 3010 || sk.id === 3020) continue;
          if (useWhitelist && !whitelist.includes(sk.id)) continue;
          if (this.cd.isReady(sk.id)) {
            plannedSkill = sk;
            break;
          }
        }
      }
      const plannedRange =
        plannedSkill && plannedSkill.rangeM && plannedSkill.rangeM > 0
          ? plannedSkill.rangeM * 32
          : classRange(klass);

      if (this.localPlayer && this.targetMonster) {
        const adminKeepSpacing =
          settings.adminKeepSpacing && getAdminSnapshot().isAdmin;
        const stopAt = Math.max(8, plannedRange - 16);
        const dxT = this.targetMonster.x - this.localPlayer.x;
        const dyT = this.targetMonster.y - this.localPlayer.y;
        const dT = Math.hypot(dxT, dyT) || 1;

        // Admin kiting: maintain a band [safeMin, safeMax] from the target.
        // safeMin is just outside the monster's attack range; safeMax is just
        // inside our (planned) attack range. If the band doesn't exist
        // (player's range <= monster's range), fall back to vanilla behaviour.
        const KITE_BUFFER = 16;
        const safeMin = MONSTER_ATTACK_RANGE + KITE_BUFFER;
        const safeMax = plannedRange - KITE_BUFFER;
        const canKite = adminKeepSpacing && safeMax > safeMin;

        if (canKite && dT < safeMin) {
          // Too close — back away from the monster along the away vector.
          const cspeed = 0.25 * delta;
          const need = safeMin - dT;
          const step = Math.min(cspeed, need);
          let nx = Phaser.Math.Clamp(
            this.localPlayer.x - (dxT / dT) * step,
            16,
            FIELD_PIX_W - 16,
          );
          let ny = Phaser.Math.Clamp(
            this.localPlayer.y - (dyT / dT) * step,
            16,
            FIELD_PIX_H - 16,
          );
          const c1 = this.collide(
            this.localPlayer.x,
            this.localPlayer.y,
            nx,
            ny,
          );
          nx = c1.x;
          ny = c1.y;
          this.localPlayer.x = nx;
          this.localPlayer.y = ny;
          useCharacterStore.getState().setPosition({ x: nx, y: ny });
          if (time - this.lastSent > 100) {
            this.lastSent = time;
            // Face the monster while retreating so attacks still aim correctly.
            const dir =
              Math.abs(dxT) > Math.abs(dyT)
                ? dxT > 0
                  ? 1
                  : 3
                : dyT > 0
                  ? 2
                  : 0;
            ensureRealtime()
              .then((cli) => cli.notify(MOVE, { x: nx, y: ny, d: dir }))
              .catch(() => {
                /* swallow */
              });
          }
          // Don't return: still try to attack while retreating if the monster
          // remains in our attack range.
        } else if (dT > (canKite ? safeMax : stopAt)) {
          const cspeed = 0.25 * delta;
          const limit = canKite ? safeMax : stopAt;
          const step = Math.min(cspeed, dT - limit);
          let nx = Phaser.Math.Clamp(
            this.localPlayer.x + (dxT / dT) * step,
            16,
            FIELD_PIX_W - 16,
          );
          let ny = Phaser.Math.Clamp(
            this.localPlayer.y + (dyT / dT) * step,
            16,
            FIELD_PIX_H - 16,
          );
          const c2 = this.collide(
            this.localPlayer.x,
            this.localPlayer.y,
            nx,
            ny,
          );
          nx = c2.x;
          ny = c2.y;
          this.localPlayer.x = nx;
          this.localPlayer.y = ny;
          useCharacterStore.getState().setPosition({ x: nx, y: ny });
          if (time - this.lastSent > 100) {
            this.lastSent = time;
            const dir =
              Math.abs(dxT) > Math.abs(dyT)
                ? dxT > 0
                  ? 1
                  : 3
                : dyT > 0
                  ? 2
                  : 0;
            ensureRealtime()
              .then((cli) => cli.notify(MOVE, { x: nx, y: ny, d: dir }))
              .catch(() => {
                /* swallow */
              });
          }
          return;
        }
      }

      let casted = false;
      if (plannedSkill) {
        const idx = skills.indexOf(plannedSkill);
        if (idx >= 0) {
          this.combat.castHotkey(idx);
          casted = true;
        }
      }
      if (!casted && this.cd.isReady(this.BASIC_ATTACK_ID)) {
        this.combat.basicAttack();
      }
    }
  }
}
